import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Star, PawPrint, Car, Wifi, ArrowDown } from "lucide-react";
import { useLang, scrollToId } from "@/i18n";
import { waLink } from "@/api";

const MaskedLine = ({ children, delay = 0, className = "" }) => (
  <span className={`masked-line ${className}`}>
    <motion.span
      className="block"
      initial={{ y: "115%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

export default function Hero({ config, prefill, setPrefill }) {
  const { t } = useLang();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yImg = useTransform(scrollYProgress, [0, 1], [0, 90]);
  const yBadge = useTransform(scrollYProgress, [0, 1], [0, 40]);

  const set = (k, v) => setPrefill((p) => ({ ...p, [k]: v }));
  const wa = config?.whatsapp || "40721663181";

  return (
    <section id="top" ref={ref} className="relative grain overflow-hidden pt-32 md:pt-40 pb-10" data-testid="hero-section">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-6 items-center">
          <div className="lg:col-span-6 relative z-10">
            <motion.p
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.15 }}
              className="text-xs md:text-sm tracking-[0.25em] uppercase font-bold text-walnut flex items-center gap-3"
              data-testid="hero-eyebrow"
            >
              <span className="inline-block w-10 h-px bg-terracotta" />
              {t("hero.eyebrow")}
            </motion.p>

            <h1 className="mt-6 font-serif font-medium text-main text-5xl md:text-7xl lg:text-[5.2rem] leading-[1.02] tracking-tight text-balance" data-testid="hero-title">
              <MaskedLine delay={0.25}>{t("hero.h1a")}</MaskedLine>
              <MaskedLine delay={0.4}>
                <span className="italic text-terracotta">{t("hero.h1b")}</span>
              </MaskedLine>
            </h1>

            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.65 }}
              className="mt-7 text-base md:text-lg leading-relaxed text-brown font-light max-w-xl"
              data-testid="hero-subtitle"
            >
              {t("hero.sub")}
            </motion.p>

            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 1, delay: 0.85 }}
              className="mt-4 font-serif italic text-lg text-green-dark"
              data-testid="hero-slogan"
            >
              „{t("hero.slogan")}"
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.95 }}
              className="mt-9 flex flex-wrap items-center gap-4"
            >
              <button
                onClick={() => scrollToId("#rezervare")}
                data-testid="hero-cta-availability"
                className="rounded-full bg-terracotta text-cream-light font-semibold px-7 py-3.5 hover:bg-terracotta/90 hover:scale-[1.03] transition-all duration-300 shadow-lg shadow-terracotta/20"
              >
                {t("hero.cta1")}
              </button>
              <a
                href={waLink(wa, "Bună, Mihai!")}
                target="_blank"
                rel="noreferrer"
                data-testid="hero-cta-whatsapp"
                className="rounded-full border border-brown/30 text-brown font-semibold px-7 py-3.5 hover:border-terracotta hover:text-terracotta transition-all duration-300"
              >
                {t("hero.cta2")}
              </a>
            </motion.div>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 1, delay: 1.15 }}
              className="mt-10 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-brown/80"
              data-testid="hero-trustbar"
            >
              <span className="flex items-center gap-1.5">
                <Star size={15} className="text-terracotta fill-terracotta" />
                <strong className="font-semibold text-main">9,6/10</strong> {t("hero.trust")}
              </span>
              <span className="hidden sm:inline text-warm-grey">·</span>
              <span>{t("rev.cleanliness")} <strong className="text-main">9,9</strong></span>
              <span className="hidden sm:inline text-warm-grey">·</span>
              <span className="flex items-center gap-1.5"><PawPrint size={15} className="text-green-dark" /> pet friendly</span>
              <span className="hidden sm:inline text-warm-grey">·</span>
              <span className="flex items-center gap-1.5"><Car size={15} className="text-green-dark" /> parking</span>
              <span className="hidden sm:inline text-warm-grey">·</span>
              <span className="flex items-center gap-1.5"><Wifi size={15} className="text-green-dark" /> Wi-Fi</span>
            </motion.div>
          </div>

          <div className="lg:col-span-6 relative">
            <motion.div
              initial={{ opacity: 0, scale: 0.96, y: 30 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              transition={{ duration: 1.2, delay: 0.35, ease: [0.22, 1, 0.36, 1] }}
              className="relative"
            >
              <motion.div style={{ y: yImg }} className="relative overflow-hidden rounded-t-[180px] md:rounded-t-[240px] rounded-b-3xl aspect-[4/5] max-h-[640px] w-full">
                <img
                  src="/assets/garden-fire.jpg"
                  alt="Grădina Imperfect House la apus, cu foc de tabără"
                  className="w-full h-full object-cover scale-105"
                  data-testid="hero-image"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-main/25 via-transparent to-transparent" />
              </motion.div>

              <motion.div
                style={{ y: yBadge }}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 1, delay: 1 }}
                className="absolute -left-3 md:-left-8 bottom-10 bg-cream-light/95 backdrop-blur border border-warm-grey/50 rounded-2xl px-6 py-5 shadow-xl shadow-main/10"
                data-testid="hero-score-badge"
              >
                <p className="font-serif text-4xl md:text-5xl text-main leading-none">9,6</p>
                <p className="mt-1 text-xs tracking-[0.15em] uppercase text-walnut font-semibold">{t("hero.badge")}</p>
              </motion.div>
            </motion.div>
          </div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 1.2 }}
          className="relative z-20 mt-12 lg:-mt-10"
          data-testid="booking-widget"
        >
          <div className="bg-cream-light border border-warm-grey/50 rounded-2xl shadow-2xl shadow-main/10 p-5 md:p-6">
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-7 gap-4 items-end">
              <label className="flex flex-col gap-1.5">
                <span className="text-[11px] tracking-[0.15em] uppercase font-bold text-walnut">{t("widget.checkin")}</span>
                <input type="date" value={prefill.checkIn} onChange={(e) => set("checkIn", e.target.value)} data-testid="widget-checkin"
                  className="bg-cream border border-warm-grey/60 rounded-lg px-3 py-2.5 text-sm text-main focus:outline-none focus:ring-2 focus:ring-terracotta/50" />
              </label>
              <label className="flex flex-col gap-1.5">
                <span className="text-[11px] tracking-[0.15em] uppercase font-bold text-walnut">{t("widget.checkout")}</span>
                <input type="date" value={prefill.checkOut} onChange={(e) => set("checkOut", e.target.value)} data-testid="widget-checkout"
                  className="bg-cream border border-warm-grey/60 rounded-lg px-3 py-2.5 text-sm text-main focus:outline-none focus:ring-2 focus:ring-terracotta/50" />
              </label>
              <label className="flex flex-col gap-1.5">
                <span className="text-[11px] tracking-[0.15em] uppercase font-bold text-walnut">{t("widget.adults")}</span>
                <select value={prefill.adults} onChange={(e) => set("adults", +e.target.value)} data-testid="widget-adults"
                  className="bg-cream border border-warm-grey/60 rounded-lg px-3 py-2.5 text-sm text-main focus:outline-none focus:ring-2 focus:ring-terracotta/50">
                  {[1, 2, 3, 4, 5, 6, 7, 8].map((n) => <option key={n} value={n}>{n}</option>)}
                </select>
              </label>
              <label className="flex flex-col gap-1.5">
                <span className="text-[11px] tracking-[0.15em] uppercase font-bold text-walnut">{t("widget.children")}</span>
                <select value={prefill.children} onChange={(e) => set("children", +e.target.value)} data-testid="widget-children"
                  className="bg-cream border border-warm-grey/60 rounded-lg px-3 py-2.5 text-sm text-main focus:outline-none focus:ring-2 focus:ring-terracotta/50">
                  {[0, 1, 2, 3, 4].map((n) => <option key={n} value={n}>{n}</option>)}
                </select>
              </label>
              <label className="flex flex-col gap-1.5">
                <span className="text-[11px] tracking-[0.15em] uppercase font-bold text-walnut">{t("widget.type")}</span>
                <select value={prefill.unitType} onChange={(e) => set("unitType", e.target.value)} data-testid="widget-type"
                  className="bg-cream border border-warm-grey/60 rounded-lg px-3 py-2.5 text-sm text-main focus:outline-none focus:ring-2 focus:ring-terracotta/50">
                  <option value="room">{t("widget.room")}</option>
                  <option value="house">{t("widget.house")}</option>
                </select>
              </label>
              <label className="flex items-center gap-2 pb-2.5 cursor-pointer">
                <input type="checkbox" checked={prefill.pet} onChange={(e) => set("pet", e.target.checked)} data-testid="widget-pet"
                  className="w-4 h-4 accent-[#C67A5C]" />
                <span className="text-sm text-brown">{t("widget.pet")}</span>
              </label>
              <button
                onClick={() => scrollToId("#rezervare")}
                data-testid="widget-submit"
                className="col-span-2 md:col-span-1 rounded-full bg-main text-cream-light font-semibold px-5 py-3 text-sm hover:bg-terracotta transition-colors duration-300 flex items-center justify-center gap-2"
              >
                {t("widget.cta")} <ArrowDown size={15} />
              </button>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
